import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import UserService from "../../services/UserService";
import {
  getProfileSuccess,
  getProfileLoading,
  getProfileFailure
} from "../../containers/Login/rolesActions";
import useUserProfileStore from "../../zustandStores/userProfileStore";
import ProfileService from "../../services/ProfileService";

interface UserProfileOptions {
  callback?: (data: any) => void;
}

/**
 * Custom hook to fetch user profile data
 * @param {UserProfileOptions} options - Options for the query
 * @param {Function} options.callback - Callback function to execute on successful fetch
 * @returns {Object} The query result including loading state, error, and data
 */
export const useUserProfile = (options: UserProfileOptions = {}) => {
  const { callback } = options;
  const dispatch = useDispatch();
  const isAuthenticated = UserService.isAuthenticated();

  // Helper function to fetch profile data
  const fetchProfileData = async (): Promise<any> => {
    dispatch(getProfileLoading());
    if (useUserProfileStore) {
      useUserProfileStore.getState().setProfileLoading();
    }

    try {
      const response = await ProfileService.getProfile();
      // Update Redux state
      dispatch(getProfileSuccess(response));

      // Also update Zustand store if available
      if (useUserProfileStore && response.data) {
        useUserProfileStore.getState().setProfileSuccess(response.data);
      }

      return response;
    } catch (error: any) {
      // Handle error
      const errorMessage = error?.message || "Failed to fetch profile";
      dispatch(getProfileFailure(errorMessage));

      // Update Zustand store error state if available
      if (useUserProfileStore) {
        useUserProfileStore.getState().setProfileError(errorMessage);
      }

      throw error;
    }
  };

  // Define query options
  const queryOptions = {
    queryKey: ["userProfile"],
    queryFn: fetchProfileData,
    staleTime: 5000, // 5 seconds stale time as requested
    enabled: isAuthenticated,
    refetchOnWindowFocus: false
  };

  const result = useQuery(queryOptions);

  // Handle callback with useEffect to ensure it's called with cached data too
  useEffect(() => {
    // Only call callback when data is available and not in the middle of refetching (which would mean stale data)
    if (callback && result.data && !result.isFetching) {
      callback(result.data.data);
    }
  }, [result.data, result.isFetching]);

  return result;
};
